import { motion, useInView } from 'framer-motion';
import { useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { 
  Layers, Cog, Target, ArrowRight,
  Building, Server, Shield, Phone, Zap,
  Eye, BarChart, RefreshCw,
  Users, GitMerge, ClipboardCheck, LineChart
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { BrandDot, ProcessFlowConnector } from '../components/brand/BrandMotif';

const buildItems = [
  { icon: Building, title: 'Digital Architecture', desc: 'Capability design and revenue model structuring' },
  { icon: Server, title: 'Infrastructure', desc: 'Network services, data centers, and cloud services' },
  { icon: Shield, title: 'Security & Communications', desc: 'Cybersecurity frameworks and unified communications' },
  { icon: Zap, title: 'Integration', desc: 'System integration across platforms and technologies' },
];

const operateItems = [
  { icon: Cog, title: 'Managed Services', desc: 'Operational management and monitoring' },
  { icon: Eye, title: 'Oversight', desc: 'Security and compliance oversight' },
  { icon: BarChart, title: 'Visibility', desc: 'Performance monitoring and dashboards' },
  { icon: RefreshCw, title: 'Evolution', desc: 'Continuous improvement and innovation' },
];

const orchestrateItems = [
  { icon: Users, title: 'OEM Alignment', desc: 'Managing vendor ecosystems to a unified standard' },
  { icon: GitMerge, title: 'Integration', desc: 'Cross-functional alignment across departments' },
  { icon: ClipboardCheck, title: 'Governance', desc: 'Outcome-based business reviews' },
  { icon: LineChart, title: 'Visibility', desc: 'Executive-level reporting and strategic oversight' },
];

// Animated Green Arrow Connector - 7 dots in logo arrow shape pointing down
const AnimatedArrowConnector = ({ isInView, delay = 0 }) => {
  // Compact 7-dot arrow pointing downward (matching logo pattern)
  // Shape:    ●      (1 dot - top)
  //         ● ● ●    (3 dots - wide)
  //          ● ●     (2 dots)
  //           ●      (1 dot - point)
  const dots = [
    // Row 1: 1 dot (top)
    { cx: 24, cy: 6, delay: 0 },
    // Row 2: 3 dots (wide)
    { cx: 12, cy: 18, delay: 0.08 },
    { cx: 24, cy: 18, delay: 0.12 },
    { cx: 36, cy: 18, delay: 0.08 },
    // Row 3: 2 dots
    { cx: 18, cy: 30, delay: 0.2 },
    { cx: 30, cy: 30, delay: 0.2 },
    // Row 4: 1 dot (point)
    { cx: 24, cy: 42, delay: 0.3 },
  ];

  return (
    <div className="flex justify-center py-3">
      <svg width="48" height="48" viewBox="0 0 48 48" fill="none">
        {dots.map((dot, index) => (
          <motion.circle
            key={index}
            cx={dot.cx}
            cy={dot.cy}
            r={5}
            fill="#4DB848"
            initial={{ opacity: 0, scale: 0 }}
            animate={isInView ? { opacity: 1, scale: 1 } : { opacity: 0, scale: 0 }}
            transition={{ 
              delay: delay + dot.delay,
              duration: 0.3,
              ease: "easeOut"
            }}
          />
        ))}
      </svg>
    </div>
  );
};

// Horizontal Arrow - 7 dots pointing right (for horizontal framework)
const HorizontalArrowConnector = ({ isInView, delay = 0 }) => {
  // Compact 7-dot arrow pointing right (rotated logo pattern)
  // Shape:  ●          
  //       ● ● ● →     
  //         ●         
  const dots = [
    // Col 1: 1 dot (left base)
    { cx: 6, cy: 24, delay: 0 },
    // Col 2: 3 dots (wide)
    { cx: 18, cy: 12, delay: 0.08 },
    { cx: 18, cy: 24, delay: 0.12 },
    { cx: 18, cy: 36, delay: 0.08 },
    // Col 3: 2 dots
    { cx: 30, cy: 18, delay: 0.2 },
    { cx: 30, cy: 30, delay: 0.2 },
    // Col 4: 1 dot (point)
    { cx: 42, cy: 24, delay: 0.3 },
  ];

  return (
    <div className="flex items-center px-2">
      <svg width="48" height="48" viewBox="0 0 48 48" fill="none">
        {dots.map((dot, index) => (
          <motion.circle
            key={index}
            cx={dot.cx}
            cy={dot.cy}
            r={5}
            fill="#4DB848"
            initial={{ opacity: 0, scale: 0 }}
            animate={isInView ? { opacity: 1, scale: 1 } : { opacity: 0, scale: 0 }}
            transition={{ 
              delay: delay + dot.delay,
              duration: 0.3,
              ease: "easeOut"
            }}
          />
        ))}
      </svg>
    </div>
  );
};

const AnimatedStep = ({ step, index, color, icon: Icon, items, isInView }) => {
  const [isHovered, setIsHovered] = useState(false);
  
  return (
    <motion.div
      initial={{ opacity: 0, y: 50 }}
      animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 50 }}
      transition={{ duration: 0.6, delay: index * 0.4 }}
      className="relative"
      data-testid={`modus-step-${step.toLowerCase()}`}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <div 
        className="bg-white border-2 shadow-lg hover:shadow-2xl transition-all duration-300 overflow-hidden"
        style={{ borderColor: color }}
      >
        {/* Header */}
        <div 
          className="p-6 flex items-center gap-4"
          style={{ backgroundColor: color }}
        >
          <div className="w-14 h-14 bg-white/20 flex items-center justify-center">
            <Icon className="w-8 h-8 text-white" />
          </div>
          <div>
            <span className="font-inter text-xs text-white/80 uppercase tracking-wider">
              Phase {index + 1}
            </span>
            <h3 className="font-montserrat font-bold text-2xl text-white">
              {step}
            </h3>
          </div>
        </div>

        {/* Content */}
        <div className="p-6">
          <div className="grid sm:grid-cols-2 gap-4">
            {items.map((item, idx) => (
              <motion.div
                key={item.title}
                initial={{ opacity: 0, x: -20 }}
                animate={isInView ? { opacity: 1, x: 0 } : { opacity: 0, x: -20 }}
                transition={{ duration: 0.4, delay: index * 0.4 + idx * 0.1 + 0.3 }}
                className="flex items-start gap-3 p-3 bg-slate-50 hover:bg-slate-100 transition-colors"
              >
                <item.icon className="w-5 h-5 flex-shrink-0 mt-0.5" style={{ color }} />
                <div>
                  <p className="font-montserrat font-semibold text-sm text-[#0B1E2D]">
                    {item.title}
                  </p>
                  <p className="font-inter text-xs text-slate-500 mt-1">
                    {item.desc}
                  </p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>

        {/* Brand motif hover accent */}
        <motion.div
          className="h-2 flex justify-center items-center gap-1 bg-slate-50"
          initial={{ opacity: 0 }}
          animate={{ opacity: isHovered ? 1 : 0 }}
          transition={{ duration: 0.3 }}
        >
          {[1.5, 2, 2.5, 3, 2.5, 2, 1.5].map((size, i) => (
            <motion.span 
              key={i}
              className="rounded-full bg-[#4DB848]"
              style={{ width: size * 3, height: size * 3 }}
              initial={{ scale: 0 }}
              animate={{ scale: isHovered ? 1 : 0 }}
              transition={{ delay: i * 0.03 }}
            />
          ))}
        </motion.div>
      </div>
    </motion.div>
  );
};

export const ModusOperandiPage = () => {
  const animationRef = useRef(null);
  const isInView = useInView(animationRef, { once: true, amount: 0.2 });
  const horizontalRef = useRef(null);
  const isHorizontalInView = useInView(horizontalRef, { once: true, amount: 0.3 });

  return (
    <div data-testid="modus-operandi-page">
      {/* Hero Banner - 1920 × 420px slim banner */}
      <section 
        className="relative flex items-center"
        style={{
          backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.2), rgba(0, 0, 0, 0.3)), url('/assets/banners/Modus_Operandi_Banner.png')`,
          backgroundSize: 'cover',
          backgroundPosition: 'center right',
          minHeight: '420px',
          height: '420px',
        }}
      >
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-16 w-full">
          <motion.div 
            className="max-w-lg"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <h1 className="font-montserrat font-bold text-3xl md:text-4xl text-white mb-4">
              The SGA Transformation Method
            </h1>
            <p className="font-inter text-base text-white leading-relaxed">
              A structured approach ensures transformation is designed, 
              operationalized and sustained.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Introduction */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="max-w-3xl">
            <span className="font-montserrat font-semibold text-sm uppercase tracking-widest text-[#0087B4] mb-4 block">
              Our Framework
            </span>
            <h2 className="heading-section text-[#0B1E2D] mb-6">
              Structured Execution Framework
            </h2>
            <p className="body-text">
              SGA STRAT operates through a structured execution framework that integrates 
              strategic design, operational management, and ecosystem orchestration. This 
              framework ensures organisations move beyond planning transformation to 
              delivering measurable business outcomes.
            </p>
          </div>
        </div>
      </section>

      {/* Framework Diagram - Horizontal with animated arrows */}
      <section className="py-16 bg-[#F8FAFC]" ref={horizontalRef}>
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="bg-white border border-slate-200 shadow-lg overflow-hidden">
            {/* Business Outcomes Bar */}
            <div className="bg-[#0B1E2D] p-6">
              <p className="font-montserrat font-semibold text-sm uppercase tracking-widest text-[#4DB848] mb-3">
                Business Outcomes
              </p>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {['Revenue Acceleration', 'Operational Efficiency', 'Governance', 'Digital Maturity'].map((outcome, idx) => (
                  <div key={outcome} className="text-center flex items-center justify-center gap-2">
                    <BrandDot size="xs" />
                    <span className="font-inter text-white text-sm">{outcome}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* BUILD | OPERATE | ORCHESTRATE with animated arrows */}
            <div className="p-6 border-b border-slate-200">
              <div className="flex flex-col md:flex-row items-center justify-center gap-2 md:gap-0">
                {[
                  { name: 'BUILD', color: '#0087B4', icon: Layers },
                  { name: 'OPERATE', color: '#4DB848', icon: Cog },
                  { name: 'ORCHESTRATE', color: '#0B1E2D', icon: Target },
                ].map((item, index) => (
                  <div key={item.name} className="flex items-center">
                    <motion.div 
                      className="text-center p-6 min-w-[200px]"
                      style={{ backgroundColor: `${item.color}10` }}
                      initial={{ opacity: 0, scale: 0.9 }}
                      animate={isHorizontalInView ? { opacity: 1, scale: 1 } : {}}
                      transition={{ delay: index * 0.3, duration: 0.5 }}
                    >
                      <item.icon className="w-10 h-10 mx-auto mb-3" style={{ color: item.color }} />
                      <span 
                        className="font-montserrat font-bold text-xl"
                        style={{ color: item.color }}
                      >
                        {item.name}
                      </span>
                    </motion.div>
                    
                    {/* Animated Arrow between steps */}
                    {index < 2 && (
                      <div className="hidden md:block">
                        <HorizontalArrowConnector 
                          isInView={isHorizontalInView} 
                          delay={index * 0.3 + 0.5}
                        />
                      </div>
                    )}
                    {index < 2 && (
                      <div className="block md:hidden py-2">
                        <AnimatedArrowConnector 
                          isInView={isHorizontalInView} 
                          delay={index * 0.3 + 0.5}
                        />
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>

            {/* Foundation Bar */}
            <div className="bg-slate-50 p-6">
              <p className="font-montserrat font-semibold text-sm uppercase tracking-widest text-[#0087B4] mb-3">
                Strategy & Leadership Foundation
              </p>
              <div className="grid grid-cols-3 gap-4">
                {['Strategy', 'Capability', 'Governance'].map((foundation, idx) => (
                  <div key={foundation} className="text-center flex items-center justify-center gap-2">
                    <BrandDot size="xs" />
                    <span className="font-inter text-slate-600">{foundation}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Animated BUILD → OPERATE → ORCHESTRATE - Vertical */}
      <section className="py-24 bg-white" ref={animationRef}>
        <div className="max-w-4xl mx-auto px-6 md:px-12">
          <div className="text-center mb-16">
            <span className="font-montserrat font-semibold text-sm uppercase tracking-widest text-[#0087B4] mb-4 block">
              The Process
            </span>
            <h2 className="heading-section text-[#0B1E2D] mb-4">
              Our Three-Phase Approach
            </h2>
            <p className="body-text max-w-2xl mx-auto">
              Each phase builds upon the previous, creating a comprehensive 
              transformation that delivers sustainable results.
            </p>
            
            {/* Brand motif divider */}
            <div className="flex items-center justify-center gap-2 mt-8">
              <div className="h-px w-16 bg-slate-200" />
              <BrandDot size="xs" />
              <BrandDot size="sm" />
              <BrandDot size="md" />
              <BrandDot size="sm" />
              <BrandDot size="xs" />
              <div className="h-px w-16 bg-slate-200" />
            </div>
          </div>

          <div className="space-y-0">
            <AnimatedStep 
              step="BUILD" 
              index={0} 
              color="#0087B4" 
              icon={Layers}
              items={buildItems}
              isInView={isInView}
            />
            
            {/* Animated Arrow Connector: BUILD → OPERATE */}
            <AnimatedArrowConnector isInView={isInView} delay={0.8} />
            
            <AnimatedStep 
              step="OPERATE" 
              index={1} 
              color="#4DB848" 
              icon={Cog}
              items={operateItems}
              isInView={isInView}
            />
            
            {/* Animated Arrow Connector: OPERATE → ORCHESTRATE */}
            <AnimatedArrowConnector isInView={isInView} delay={1.6} />
            
            <AnimatedStep 
              step="ORCHESTRATE" 
              index={2} 
              color="#0B1E2D" 
              icon={Target}
              items={orchestrateItems}
              isInView={isInView}
            />
          </div>
        </div>
      </section>

      {/* Detailed Sections */}
      <section className="py-24 bg-[#F8FAFC]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          {/* BUILD Section */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="mb-20"
          >
            <div className="flex items-center gap-4 mb-6">
              <div className="w-12 h-12 bg-[#0087B4] flex items-center justify-center">
                <Layers className="w-6 h-6 text-white" />
              </div>
              <div>
                <span className="font-inter text-sm text-[#0087B4] uppercase tracking-wider">Phase 1</span>
                <h3 className="font-montserrat font-bold text-2xl text-[#0B1E2D]">
                  BUILD: Designing the Digital Backbone
                </h3>
              </div>
            </div>
            <p className="body-text mb-8 max-w-3xl">
              SGA helps organizations design the foundational structures required for 
              transformation. This phase establishes the digital architecture, infrastructure, 
              security frameworks, and integration capabilities that form the backbone of 
              modern enterprise operations.
            </p>
          </motion.div>

          {/* OPERATE Section */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="mb-20"
          >
            <div className="flex items-center gap-4 mb-6">
              <div className="w-12 h-12 bg-[#4DB848] flex items-center justify-center">
                <Cog className="w-6 h-6 text-white" />
              </div>
              <div>
                <span className="font-inter text-sm text-[#4DB848] uppercase tracking-wider">Phase 2</span>
                <h3 className="font-montserrat font-bold text-2xl text-[#0B1E2D]">
                  OPERATE: Ensuring Continuity and Performance
                </h3>
              </div>
            </div>
            <p className="body-text mb-8 max-w-3xl">
              SGA supports organizations in managing and optimizing operational performance. 
              Through managed services, security oversight, performance monitoring, and 
              continuous improvement initiatives, we ensure that transformation investments 
              deliver sustained value.
            </p>
          </motion.div>

          {/* ORCHESTRATE Section */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <div className="flex items-center gap-4 mb-6">
              <div className="w-12 h-12 bg-[#0B1E2D] flex items-center justify-center">
                <Target className="w-6 h-6 text-white" />
              </div>
              <div>
                <span className="font-inter text-sm text-[#0B1E2D] uppercase tracking-wider">Phase 3</span>
                <h3 className="font-montserrat font-bold text-2xl text-[#0B1E2D]">
                  ORCHESTRATE: Unifying the Enterprise Ecosystem
                </h3>
              </div>
            </div>
            <p className="body-text mb-8 max-w-3xl">
              SGA's orchestration model integrates vendors, technologies, and teams into a 
              unified transformation program. This phase focuses on OEM alignment, cross-functional 
              integration, outcome-based governance, and executive-level visibility to ensure 
              sustainable transformation success.
            </p>
          </motion.div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-[#0B1E2D]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="heading-section text-white mb-6">
              Experience Our Methodology
            </h2>
            <p className="font-inter text-lg text-slate-300 mb-10 max-w-2xl mx-auto">
              Ready to transform your organization with a structured, proven approach? 
              Let's discuss how our methodology can work for you.
            </p>
            <Button 
              asChild
              data-testid="modus-cta"
              className="bg-[#4DB848] hover:bg-[#3da03a] text-white rounded-none px-10 py-6 font-montserrat font-bold tracking-wider"
            >
              <Link to="/engage">
                Start the Conversation
                <ArrowRight className="ml-2 w-5 h-5" />
              </Link>
            </Button>
          </motion.div>
        </div>
      </section>
    </div>
  );
};
